import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Alert,
  IconButton,
  CircularProgress
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import axios from '../utils/axiosConfig';
import { handleApiError } from '../utils/apiErrorHandler';
import { CompetitionInfoContext } from './RegisteredCompetitionPage';
import { useAuth } from '../context/AuthContext';

// Interfaces für Athletendaten aus der SporttechAPI
interface AthleteStartData {
  GUID: string;
  performances_G: string | null;
  firstName: string;
  lastName: string;
  representing: string;
  groupName: string | null;
  startTime: string | null;
  competitionGroup: string | null;
  startPosition: number;
  totalParticipantsInGroup: number;
  totalParticipantsInStage: number;
}

interface GroupedAthletes {
  [competitionGroup: string]: AthleteStartData[];
}

const SchedulePage: React.FC = () => {
  // Hooks
  const { competitionId } = useParams();
  const { clubId } = useAuth();

  const [loading, setLoading] = useState(true);
  
  // State für Athleten-Startzeiten
  const [athleteStartData, setAthleteStartData] = useState<AthleteStartData[]>([]);
  const [loadingAthletes, setLoadingAthletes] = useState(false);
  
  // Access the validation context
  const infoContext = useContext(CompetitionInfoContext);

  const [competitionName, setCompetitionName] = useState("Wettkampf");
  const [clubName, setClubName] = useState("Verein");

  // Funktion zum Abrufen der Startzeiten
  const fetchAthleteStartTimes = useCallback(async () => {
    if (!competitionId || !clubId) return;
    
    try {
      setLoadingAthletes(true);      
      const response = await axios.get(`/api/sporttech/athletes/${competitionId}/${clubId}`);
      const athletes = response.data.data || [];
      setAthleteStartData(athletes);
    } catch (error) {
      handleApiError(error, "Fehler beim Laden der Startzeiten.");
    } finally {
      setLoadingAthletes(false);
    }
  }, [competitionId, clubId]);

  // Gruppieren der Athleten nach Wettkampfgruppe
  const getGroupedAthletes = useCallback(() => {
    const grouped: GroupedAthletes = {};
    
    athleteStartData.forEach(athlete => {
      // Als Schlüssel verwenden wir die Wettkampfgruppe oder "Ohne Gruppe" als Fallback
      const groupKey = athlete.competitionGroup || "Ohne Wettkampfklasse";
      
      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }
      
      grouped[groupKey].push(athlete);
    });
    
    // Innerhalb jeder Gruppe sortieren nach:
    // 1. Startgruppe (groupName)
    // 2. Startposition (startPosition)
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => {
        // Primär nach Startgruppe sortieren
        if (a.groupName !== b.groupName) {
          // Null-Werte am Ende platzieren
          if (!a.groupName) return 1;
          if (!b.groupName) return -1;
          return a.groupName.localeCompare(b.groupName);
        }
        
        // Sekundär nach Startposition sortieren
        if (a.startPosition !== b.startPosition) {
          return a.startPosition - b.startPosition;
        }
        
        // Tertiär nach Nachname sortieren (als Fallback)
        return a.lastName.localeCompare(b.lastName);
      });
    });
    
    return grouped;
  }, [athleteStartData]);

  // Funktion zum Abrufen der Gesamtteilnehmerzahl pro Wettkampfklasse
  const getTotalParticipantsInStage = useCallback((competitionGroup: string): number => {
    if (athleteStartData.length === 0) return 0;
    
    // Finde einen Athleten dieser Wettkampfklasse, dessen totalParticipantsInStage wir zurückgeben können
    const athlete = athleteStartData.find(athlete => athlete.competitionGroup === competitionGroup);
    return athlete ? athlete.totalParticipantsInStage : 0;
  }, [athleteStartData]);

  useEffect(() => {
    const fetchCompetitionDetails = async () => {
      if (competitionId) {
        try {
          const response = await axios.get(`/api/competitions/${competitionId}`);
          setCompetitionName(response.data.name);
        } catch (error) {
          handleApiError(error, "Fehler beim Laden des Wettkampfnamens.");
        }
      }
      
      if (clubId) {
        try {
          const response = await axios.get(`/api/users/me`);
          setClubName(response.data.club.name);
        } catch (error) {
          handleApiError(error, "Fehler beim Laden des Vereinsnamens.");
        }
      }
    };
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Startzeiten der Athleten laden
        await fetchAthleteStartTimes();
        setLoading(false);
      } catch (error) {
        handleApiError(error, 'Fehler beim Laden der Startzeiten.');
        setLoading(false);
      }
    };

    if (competitionId && clubId) {
      fetchCompetitionDetails();
      fetchData();
    }
  }, [competitionId, clubId, fetchAthleteStartTimes]);

  const PrintHeader = () => {
    return (
      <Box className="print-header" sx={{ display: 'none' }}>
        <Typography variant="h5" align="center" gutterBottom>
          {competitionName}
        </Typography>
        <Typography variant="h6" align="center" gutterBottom>
          Startzeiten für {clubName}
        </Typography>
        <Typography variant="h6" align="center" sx={{ mb: 3 }}>
          Stand: {new Date().toLocaleDateString()}
        </Typography>
      </Box>
    );
  };

  const handlePrint = useCallback(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        @page {
          margin: 0.5cm;
        }
        body * {
          visibility: hidden;
        }
        .print-container, .print-container * {
          visibility: visible;
        }
        .print-container {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          font-size: 9px !important;
        }
        .print-header {
          display: block !important;
          margin-bottom: 8px;
        }
        .print-header h5 {
          font-size: 14px !important;
          margin-bottom: 4px !important;
        }
        .print-header h6 {
          font-size: 12px !important;
          margin-bottom: 2px !important;
        }
        /* Extrem kompakte Tabellen */
        .athlete-tables table {
          font-size: 8px !important;
          border-collapse: collapse !important;
        }
        .athlete-tables th, .athlete-tables td {
          padding: 2px 4px !important;
          line-height: 1.2 !important;
        }
        .athlete-tables tr {
          height: 16px !important;
        }
        /* Schmalere Spaltenbreiten */
        .athlete-tables .name-cell {
          max-width: 400px !important;
        }
        .athlete-tables .group-cell {
          max-width: 80px !important;
        }
        .athlete-tables .time-cell {
          width: 200px !important;
        }
        .athlete-tables .position-cell {
          width: 60px !important;
        }
        /* Gruppenüberschriften minimiert */
        .athlete-tables .group-header {
          padding: 2px 4px !important;
          font-size: 10px !important;
          margin-bottom: 0 !important;
        }
        /* Minimaler Abstand zwischen Gruppen */
        .athlete-tables .group-container {
          margin-bottom: 6px !important;
        }
        /* Mehrspaltige Ansicht */
        @media (min-width: 800px) {
          .athlete-tables {
            column-count: 2;
            column-gap: 20px;
          }
          .group-container {
            break-inside: avoid;
          }
        }
      }
    `;
    document.head.appendChild(style);
    window.print();
    setTimeout(() => document.head.removeChild(style), 1000);
  }, []);
  

  // Komponente für die Anzeige der Athleten-Startzeiten
  const AthleteStartTimesTable = () => {
    const groupedAthletes = getGroupedAthletes();
    const groups = Object.keys(groupedAthletes).sort();
    
    if (loadingAthletes) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (athleteStartData.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          Keine Startzeiten verfügbar. Die Informationen werden möglicherweise später aktualisiert.
        </Alert>
      );
    }
    
    return (
      <Box className="print-container">
        <PrintHeader />
      <Box sx={{ mt: 3 }} className="athlete-tables">
        {groups.map(competitionGroup => (
          <Box key={competitionGroup} className="group-container" sx={{ mb: 4 }}>
            <Typography 
              className="group-header"
              variant="subtitle1" 
              sx={{ 
                bgcolor: 'primary.main', 
                color: 'white', 
                p: 1, 
                borderRadius: '4px 4px 0 0' 
              }}
            >
              {competitionGroup} ({getTotalParticipantsInStage(competitionGroup)} TN)
            </Typography>
            
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Gruppe</TableCell>
                    <TableCell>Startzeit</TableCell>
                    <TableCell>Startposition</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedAthletes[competitionGroup].map((athlete, index) => (
                    <TableRow key={`${athlete.GUID}-${athlete.performances_G ?? index}`}>
                      <TableCell className="name-cell">{athlete.firstName} {athlete.lastName}</TableCell>
                      <TableCell className="group-cell">{athlete.groupName ? `${athlete.groupName}` : "-"}</TableCell>
                      <TableCell className="time-cell">{athlete.startTime || "Noch nicht festgelegt"}</TableCell>
                      <TableCell className="position-cell">{athlete.startPosition ? `${athlete.startPosition} von ${athlete.totalParticipantsInGroup}` : "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}
      </Box>
      </Box>
    );
  };

  return (
    <Container component="main" maxWidth="md">
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">
            Startzeiten
          </Typography>
          <Box>
            {/*<IconButton aria-label="download schedule">
              <DownloadIcon />
            </IconButton>*/}
            <IconButton aria-label="print schedule" onClick={handlePrint}>
              <PrintIcon />
            </IconButton>
          </Box>
        </Box>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          Die Startzeiten können kurzfristig noch angepasst werden. Bitte prüft regelmäßig auf Aktualisierungen.
        </Alert>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <AthleteStartTimesTable />
        )}
      </Paper>
    </Container>
  );
};

export default SchedulePage;